import React, { useState } from 'react';
import { toNumberOrMinusOne } from 'utils/helpers';
import { PERMISSIONS } from 'constants/permissions';
import { CalendarIcon } from 'components/common/icons';
import { ValidationErrors } from 'constants/validations';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { ContactPersonData } from 'components/common/contactPerson/contactPersonCard';
import { Col, DatePicker, Form, Input, Row, Select, SupplierSelect, Typography } from 'components';
import { AttachmentForm } from './attachments';
import styles from './../styles.module.scss';

const { Text } = Typography;

interface IGeneralInformation {
  attachments: { type: string; file: File }[];
  setAttachments: (attachments: { type: string; file: File }[]) => void;
  handleFormItemBlur: (fieldKey: string, value: string | number) => void;
  handleFormItemChange: (fieldKey: string, value: string) => void;
  setOpenContactPersonModal: (value: boolean) => void;
  onAttachmentAdd: (attachments: { type: string; file: File }[]) => void;
  onAttachmentDelete: (ids: string[]) => void;
  isLoadingContactPerson: boolean;
  contactPersons: ContactPersonData[];
}

export const GeneralInformation = React.memo((props: IGeneralInformation) => {
  const { hasAccess } = useAccessControl();
  const [fileList, setFileList] = useState<File[]>([]);
  const [attachmentType, setAttachmentType] = useState<string | undefined>();

  const {
    attachments,
    setAttachments,
    isLoadingContactPerson,
    contactPersons,
    onAttachmentAdd,
    onAttachmentDelete,
    handleFormItemBlur,
    handleFormItemChange,
    setOpenContactPersonModal,
  } = props;
  return (
    <Row style={{ padding: '20px' }} gutter={[0, 20]}>
      <Col span={24}>
        <Text style={{ color: '#05796B' }} className={'font-weight-800 font-size-16'}>
          General Information
        </Text>
      </Col>
      <Col span={24}>
        <Row gutter={[0, 32]}>
          <Col span={24}>
            <Row gutter={[12, 20]}>
              <Col span={12}>
                <Form.Item
                  name="supplierId"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Supplier'),
                    },
                  ]}
                  label={'Supplier*'}
                >
                  <SupplierSelect onChange={(value) => handleFormItemChange('supplierId', value)} size={'large'} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="source"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Source'),
                    },
                  ]}
                  label={'Source*'}
                >
                  <Select
                    onChange={(value) => handleFormItemChange('source', value)}
                    options={[
                      { value: 'EMAIL', label: 'Email' },
                      { value: 'PRICE_LIST', label: 'Price list' },
                    ]}
                    size={'large'}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="contactPersonId"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Contact Person'),
                    },
                  ]}
                  label={'Contact Person*'}
                >
                  <Select
                    onChange={(value) => handleFormItemChange('contactPersonId', value)}
                    size={'large'}
                    loading={isLoadingContactPerson}
                    onAdd={() => setOpenContactPersonModal(true)}
                    showAdd={hasAccess(PERMISSIONS.clients.contact_person.write)}
                    options={contactPersons}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="offerNumber"
                  rules={[
                    {
                      required: true,
                      message: ValidationErrors.required('Supplier Offer Number'),
                    },
                  ]}
                  label={'Supplier Offer Number*'}
                >
                  <Input
                    onBlur={(e) => handleFormItemBlur('offerNumber', toNumberOrMinusOne(e.target.value))}
                    size={'large'}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="supplierOfferValidity" label={'Offer Validity from Supplier'}>
                  <DatePicker
                    onChange={(date) => handleFormItemChange('supplierOfferValidity', date.toISOString())}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    size={'large'}
                    suffixIcon={<CalendarIcon />}
                    popupClassName={'fmg-range-picker'}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24} className={styles.attachments}>
            <AttachmentForm
              attachments={attachments}
              setAttachments={setAttachments}
              onAdd={onAttachmentAdd}
              onDelete={onAttachmentDelete}
              fileList={fileList}
              setFileList={setFileList}
              setAttachmentType={setAttachmentType}
              attachmentType={attachmentType}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
});
